/**
 * Text validation utilities for English text fields
 */

/**
 * Validates that English text starts with a capital letter
 * @param {string} text - The text to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function startsWithCapital(text) {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (trimmed.length === 0) return false;
  return /^[A-Z]/.test(trimmed);
}

/**
 * Validates that text ends with proper punctuation (. ! ?)
 * @param {string} text - The text to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function endsWithPunctuation(text) {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (trimmed.length === 0) return false;
  return /[.!?]$/.test(trimmed);
}

/**
 * Validates that text does NOT end with a period
 * @param {string} text - The text to validate
 * @returns {boolean} - True if valid (no period at end), false otherwise
 */
export function doesNotEndWithPeriod(text) {
  if (!text || typeof text !== 'string') return true; // Empty is valid
  const trimmed = text.trim();
  if (trimmed.length === 0) return true;
  return !/\.$/.test(trimmed);
}

/**
 * Validates English sentence text (must start with capital and end with punctuation)
 * @param {string} text - The text to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} - Error message if invalid, null if valid
 */
export function validateEnglishSentence(text, fieldName = 'English text') {
  if (!text || text.trim().length === 0) {
    return null; // Empty is allowed, required validation is separate
  }

  if (!startsWithCapital(text)) {
    return `${fieldName} must start with a capital letter`;
  }

  if (!endsWithPunctuation(text)) {
    return `${fieldName} must end with . ! or ?`;
  }

  return null;
}

/**
 * Validates English word/phrase (must start with capital, must NOT end with period)
 * @param {string} text - The text to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} - Error message if invalid, null if valid
 */
export function validateEnglishWord(text, fieldName = 'English word') {
  if (!text || text.trim().length === 0) {
    return null; // Empty is allowed, required validation is separate
  }

  if (!startsWithCapital(text)) {
    return `${fieldName} must start with a capital letter`;
  }

  if (!doesNotEndWithPeriod(text)) {
    return `${fieldName} should not end with a period`;
  }

  return null;
}

/**
 * Validates English text for specific activity types
 * @param {string} activityType - The type of activity
 * @param {string} text - The text to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} - Error message if invalid, null if valid
 */
export function validateActivityEnglishText(activityType, text, fieldName = 'English text') {
  if (!text || text.trim().length === 0) {
    return null; // Empty is allowed
  }

  // Activities where English text should be a sentence (with punctuation)
  const sentenceActivities = [
    'QUESTION_ANSWERS',
    'LONG_QUESTION_ANSWERS',
    'WHO_SAID_TO_WHOM',
    'SENTENCE' // For sentence editing
  ];

  // Activities where English text should be a word/phrase (no period)
  const wordActivities = [
    'WORD_MEANING',
    'OPPOSITES',
    'WORD_PRACTICE'
  ];

  if (sentenceActivities.includes(activityType)) {
    return validateEnglishSentence(text, fieldName);
  }

  if (wordActivities.includes(activityType)) {
    return validateEnglishWord(text, fieldName);
  }

  // Default: just check capital letter
  if (!startsWithCapital(text)) {
    return `${fieldName} must start with a capital letter`;
  }

  return null;
}

/**
 * Batch validate multiple English text fields
 * @param {Array<{text: string, fieldName: string, activityType: string}>} fields
 * @returns {Array<string>} - Array of error messages
 */
export function validateMultipleEnglishTexts(fields) {
  const errors = [];
  
  for (const field of fields) {
    const error = validateActivityEnglishText(
      field.activityType,
      field.text,
      field.fieldName
    );
    if (error) {
      errors.push(error);
    }
  }
  
  return errors;
}
